import { CircularProgress } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { handleForbiddenResource } from '~/common/authUtils';
import rootStore from '~/store';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import Billing from './billing';
import General from './General';
import LeftNav from './LeftNav';
import OrgLayerLibrary from './layerLibrary';
import Members from './members';
import Plan from './plan';
import Roles from './roles';

export type OrganizationSettingsParams = {
  orgId: string;
  tabId: string;
};

const tabMap: Record<string, () => JSX.Element> = {
  general: General,
  roles: Roles,
  members: Members,
  plan: Plan,
  billing: Billing,
  layerLibrary: OrgLayerLibrary,
};

export default function OrganizationSettings() {
  const { tabId } = useParams({ strict: false }) as OrganizationSettingsParams;
  const { isUserBootstrapped } = useSnapshot(rootStore);

  const canViewOrgSettings = authorizations.can(Action.ViewOrgSettings);

  useEffect(() => {
    if (isUserBootstrapped && !canViewOrgSettings) {
      handleForbiddenResource();
    }
  }, [isUserBootstrapped, canViewOrgSettings]);

  const mainContent = useMemo(() => {
    if (tabMap[tabId]) {
      const Content = tabMap[tabId];
      return <Content />;
    } else {
      return <CircularProgress indeterminate />;
    }
  }, [tabId]);

  if (!isUserBootstrapped || !canViewOrgSettings) return <CircularProgress indeterminate />;

  return (
    <ProtectedRouteLayout>
      <div className="flex h-full w-full grow flex-row ">
        <LeftNav />
        <div className="h-screen-height-minus-top-nav w-full overflow-y-auto px-2 py-4">{mainContent}</div>
      </div>
    </ProtectedRouteLayout>
  );
}
